import React, { useState, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { propType } from 'graphql-anywhere'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useHistory } from 'react-router-dom'
import { useQuery, useMutation } from 'react-apollo'
import {
  path,
  filter,
  find,
  propEq
} from 'ramda'
import ClickOutside from 'react-click-outside'

import ACCOUNT_QUERY from '../../../../graphql/queries/account.gql'
import PROFILE_MUTATION from '../../../../graphql/mutations/profile.gql'
import NAV_ITEM_FRAGMENT from '../../../../graphql/fragments/nav-item.gql'

import NavigationDropdown from './navigation-dropdown'
import NavigationItemButton from './navigation-itembutton'

import {
  displayErrorDialog,
  setNavigationVisibility,
  setSearchIsClicked
} from '../../../actions'
import { handleSwitchProfile } from '../../../actions/profile'
import {
  MODALS, NAVIGATION_BAR_GA_KIDS_INSTANT_SWITCH, DEFAULT_KIDS_PROFILE_DETAIL
} from '../../../constants'
import { useProfile } from '../../../hooks/use-profile'
import { useMutateAndLoading } from '../../../hooks/use-mutate-and-loading'
import { navigationBarGa } from '../../../lib/analytics/ga'
import { customDimensions } from '../../../lib/analytics/custom-dimensions'
import { requirePinForSwitchProfile } from '../../../lib/account'
import { getGQLErrorMsg } from '../../../lib/apollo'
import { checkQueryStatus } from '../../../lib/utils'
import { findPathName, hasChildren } from './navigation-item-helpers'

import styles from './navigation-item.css'

const FAIL_ADDING_KIDS = 'failAddingKids'

/**
 * NavigationItem represents a single item in the navigation header, side, dropdown, or settings.
 * Handles navigation, profile switching for kids accounts, dropdown toggling, hover expansion,
 * analytics tracking, and closing of side navigation or dropdowns on outside clicks.
 */
export default function NavigationItem({
  navItem,
  className,
  activeClassName,
  inSideNav,
  wrapperClassName,
  inDropdown,
  propertiesCount,
  expandOnHover,
  expandOnArrow,
  isInsideNavigationBar,
  setParentIsExpanded
}) {
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const { currentProfile } = useProfile('cache-only')

  const [isExpanded, setIsExpanded] = useState(false)

  const { sideNav } = useSelector(state => state.navigation)

  const profileId = currentProfile.id

  // GraphQL hooks
  const { data: accountQuery, loading, error } = useQuery(ACCOUNT_QUERY, {
    skip: !(navItem.isAuthenticated && navItem.isKids)
  })
  const [mutateProfile] = useMutation(PROFILE_MUTATION)
  const mutateAndLoading = useMutateAndLoading(mutateProfile)

  const profiles = path(['account', 'profiles'], accountQuery)
  const entitlement = path(['account', 'subscription', 'entitlement'], accountQuery)
  const requirePin = accountQuery && requirePinForSwitchProfile(accountQuery.account)

  const pathname = useMemo(() => findPathName(navItem), [navItem])

  const hideSideNavigation = useCallback(() => {
    dispatch(setNavigationVisibility(false))
  }, [dispatch])

  const handleOneKidsProfile = useCallback(({ kidsProfile }) => {
    handleSwitchProfile({
      dispatch,
      currentProfile,
      targetProfile: kidsProfile,
      requirePin,
      navigationBarKidsGAOption: NAVIGATION_BAR_GA_KIDS_INSTANT_SWITCH
    })
  }, [dispatch, currentProfile, requirePin])

  const handleNonKidsProfile = useCallback(async () => {
    if (profiles?.length === entitlement?.profileLimit) {
      return FAIL_ADDING_KIDS
    }

    if (profiles && profiles.length < entitlement?.profileLimit) {
      const existingDefaultKidsProfiles = filter(propEq('name', DEFAULT_KIDS_PROFILE_DETAIL.name), profiles)
      if (existingDefaultKidsProfiles?.length === 1) {
        return FAIL_ADDING_KIDS
      }
      try {
        const profileData = await mutateAndLoading('profileAdd', {
          variables: { input: DEFAULT_KIDS_PROFILE_DETAIL }
        })
        const latestProfiles = profileData.profile.profiles
        const newKidsProfile = find(propEq('isKid', true), latestProfiles)
        handleSwitchProfile({
          dispatch, currentProfile, targetProfile: newKidsProfile, requirePin, navigationBarKidsGAOption: NAVIGATION_BAR_GA_KIDS_INSTANT_SWITCH
        })
      } catch (mutateError) {
        dispatch(displayErrorDialog({ message: getGQLErrorMsg(mutateError) }))
      }
    } else {
      return FAIL_ADDING_KIDS
    }
    return null
  }, [profiles, entitlement, mutateAndLoading, dispatch, currentProfile, requirePin])

  const handleMultipleKidsProfile = useCallback(() => (
    `?modal=${MODALS.qsParams.profilesSwitch}&nav=true`
  ), [])

  const handleFullProfiles = useCallback(() => (
    `?modal=${MODALS.qsParams.profilesSwitch}&nav=true&kids_full=true`
  ), [])

  const onClick = useCallback(async (event) => {
    if (isInsideNavigationBar) {
      navigationBarGa({
        action: navItem.name,
        [customDimensions.ProfileId]: profileId
      })
    }

    if (navItem.name === 'Search') {
      dispatch(setSearchIsClicked(true))
    }

    let nextPath = pathname

    if (navItem.isKids && navItem.isAuthenticated) {
      const allKids = filter(propEq('isKid', true), profiles)
      if (allKids.length === 1) {
        handleOneKidsProfile({ kidsProfile: allKids[0] })
      } else if (allKids.length === 0) {
        const result = await handleNonKidsProfile()
        if (result === FAIL_ADDING_KIDS) {
          nextPath = handleFullProfiles()
        }
      } else {
        nextPath = handleMultipleKidsProfile()
      }
    }

    // For all the navigation items, except Discover, we have to prevent the
    // default behavior to avoid expand the dropdowns when the site is
    // redirecting to a page
    if (!navItem.isDiscoverNav) {
      event.stopPropagation()
      event.preventDefault()
      // Except navItem is kids and path is the default,
      // because of proceeding to redirect in the switch profile
      if (!(navItem.isKids && nextPath === '/#')) {
        history.push(nextPath)
      }
    }

    // If is Discover nav, keep the side navigation opened
    if (inSideNav && !navItem.isDiscoverNav) {
      hideSideNavigation()
    }

    if (setParentIsExpanded) setParentIsExpanded(false)
  }, [
    isInsideNavigationBar,
    navItem,
    profileId,
    dispatch,
    profiles,
    handleOneKidsProfile,
    handleNonKidsProfile,
    handleMultipleKidsProfile,
    handleFullProfiles,
    pathname,
    inSideNav,
    hideSideNavigation,
    history,
    setParentIsExpanded
  ])

  const onToggleMenu = useCallback(() => {
    setIsExpanded(prev => !prev)
  }, [])

  const onClickOutside = useCallback(() => {
    if (!isExpanded) return
    if (inSideNav && sideNav) hideSideNavigation()
    setIsExpanded(false)
  }, [isExpanded, inSideNav, sideNav, hideSideNavigation])

  if (checkQueryStatus({ loading, error })) return null

  return (
    <ClickOutside
      className={
        classNames({
          [styles.navItemWrapperOpen]: isExpanded,
          [styles.navItemWrapper]: !isExpanded,
          [wrapperClassName]: wrapperClassName,
          [styles.isRental]: navItem.isRental
        })
      }
      onClickOutside={!inSideNav ? onClickOutside : () => {}}
      onMouseLeave={() => (expandOnHover ? setIsExpanded(false) : undefined)}
    >
      <NavigationItemButton
        activeClassName={activeClassName}
        location={location}
        navItem={navItem}
        onToggleMenu={onToggleMenu}
        isExpanded={isExpanded}
        pathname={pathname}
        inDropdown={inDropdown}
        className={
          classNames({
            [className]: className,
            [styles.lowercase]: inDropdown && !hasChildren(navItem)
          })
        }
        onClick={onClick}
        setIsExpanded={setIsExpanded}
        expandOnHover={expandOnHover}
        expandOnArrow={expandOnArrow}
      />
      {
        hasChildren(navItem) && isExpanded && (
          <NavigationDropdown
            navItems={navItem.children}
            inSideNav={inSideNav}
            setParentIsExpanded={setParentIsExpanded}
            setIsExpanded={setIsExpanded}
            isDiscoverNav={navItem.isDiscoverNav}
            propertiesCount={propertiesCount}
          />
        )
      }
    </ClickOutside>
  )
}

NavigationItem.propTypes = {
  navItem: propType(NAV_ITEM_FRAGMENT).isRequired,
  className: PropTypes.string,
  activeClassName: PropTypes.string,
  inSideNav: PropTypes.bool,
  wrapperClassName: PropTypes.string,
  inDropdown: PropTypes.bool,
  propertiesCount: PropTypes.number,
  expandOnArrow: PropTypes.bool,
  expandOnHover: PropTypes.bool,
  isInsideNavigationBar: PropTypes.bool,
  setParentIsExpanded: PropTypes.func
}

NavigationItem.defaultProps = {
  className: null,
  activeClassName: null,
  inSideNav: false,
  wrapperClassName: '',
  inDropdown: false,
  propertiesCount: 0,
  expandOnArrow: false,
  expandOnHover: false,
  isInsideNavigationBar: false,
  setParentIsExpanded: null
}
